// src/modules/feed/components/ActivitiesFeed/index.js
import React, { useState, useCallback } from 'react';
import { Card, Modal, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAttachments } from './hooks/useAttachments';
import { formatDate, formatDuration, calculatePace } from '../../../../shared/utils/formatters';
import './styles.css';

const getActivityIcon = (activityType) => {
    const iconMap = {
        'running': 'bi-person-walking',
        'cycling': 'bi-bicycle',
        'swimming': 'bi-water',
        'hiking': 'bi-tree',
        'walking': 'bi-person-walking',
        'football': 'bi-trophy',
        'badminton': 'bi-circle',
        'other': 'bi-activity'
    };
    return iconMap[activityType?.toLowerCase()] || 'bi-activity';
};

const getActivityIconColor = (activityType) => {
    const colorMap = {
        'running': 'text-danger',
        'cycling': 'text-primary',
        'swimming': 'text-info',
        'hiking': 'text-success',
        'walking': 'text-secondary',
        'football': 'text-warning',
        'badminton': 'text-primary',
        'other': 'text-muted'
    };
    return colorMap[activityType?.toLowerCase()] || 'text-muted';
};

const FeedActivityCard = ({ activity, onUsernameClick, onImageClick, resolvedAttachments }) => {
    const activityType = activity.activityType?.charAt(0).toUpperCase() + activity.activityType?.slice(1);
    const userName = activity.userGivenName || `${activity.firstName} ${activity.lastName}`;

    // Resolve attachment URLs
    const attachmentImages = activity.attachments?.map(att => ({
        ...att,
        url: resolvedAttachments[att.url] || att.url
    })) || [];

    const hasImages = activity.photos?.length > 0 || attachmentImages.length > 0;

    return (
        <div className="feed-activity-item">
            <div className="feed-activity-header">
                <div
                    className="feed-activity-avatar clickable"
                    onClick={(e) => {
                        e.preventDefault();
                        onUsernameClick?.(activity.username);
                    }}
                >
                    {activity.profilePicture ? (
                        <Image
                            src={activity.profilePicture}
                            roundedCircle
                            alt={userName}
                        />
                    ) : (
                        <div className="avatar-placeholder">
                            <i className="bi bi-person"></i>
                        </div>
                    )}
                </div>
                <div className="feed-activity-user-info">
                    <span
                        className="feed-activity-username clickable"
                        onClick={(e) => {
                            e.preventDefault();
                            onUsernameClick?.(activity.username);
                        }}
                    >
                        {userName}
                    </span>
                    <span className="feed-activity-type">
                        <i className={`bi ${getActivityIcon(activity.activityType)} ${getActivityIconColor(activity.activityType)} me-1`}></i>
                        {activityType}
                    </span>
                </div>
                <div className="feed-activity-points">
                    <span className="points-value">{parseFloat(activity.points).toFixed(1)}</span>
                    <span className="points-label">points</span>
                </div>
            </div>

            <Link to={`/activity/${activity.activityId}`} className="feed-activity-content">
                <div className="feed-activity-stats-row">
                    <div className="feed-stat-item">
                        <i className="bi bi-geo-alt"></i>
                        <span>{parseFloat(activity.distance).toFixed(1)} km</span>
                    </div>
                    <div className="feed-stat-item">
                        <i className="bi bi-clock"></i>
                        <span>{formatDuration(activity.duration)}</span>
                    </div>
                    <div className="feed-stat-item">
                        <i className="bi bi-speedometer2"></i>
                        <span>{calculatePace(activity.duration, activity.distance, activity.activityType)}</span>
                    </div>
                    {activity.elevation > 0 && (
                        <div className="feed-stat-item">
                            <i className="bi bi-arrow-up-right"></i>
                            <span>{parseFloat(activity.elevation).toFixed(0)} m</span>
                        </div>
                    )}
                    {activity.calories > 0 && (
                        <div className="feed-stat-item">
                            <i className="bi bi-fire"></i>
                            <span>{activity.calories.toFixed(0)} kcal</span>
                        </div>
                    )}
                </div>

                {activity.description && (
                    <div className="feed-activity-description">
                        {activity.description}
                    </div>
                )}

                {hasImages && (
                    <div className="feed-activity-images">
                        {activity.photos?.map((photo, index) => (
                            <div
                                key={`photo-${index}`}
                                className="feed-activity-thumbnail"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onImageClick?.(photo);
                                }}
                            >
                                <Image src={photo} alt="Activity photo" />
                            </div>
                        ))}
                        {attachmentImages.map((attachment, index) => (
                            <div
                                key={`attachment-${index}`}
                                className="feed-activity-thumbnail"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onImageClick?.(attachment.url);
                                }}
                            >
                                <Image src={attachment.url} alt={attachment.name} />
                            </div>
                        ))}
                    </div>
                )}
            </Link>
        </div>
    );
};

const ActivitiesFeed = ({ activities, onUsernameClick }) => {
    const [enlargedImage, setEnlargedImage] = useState(null);
    const { attachments } = useAttachments(activities);

    const handleImageClick = useCallback((src) => {
        setEnlargedImage(src);
    }, []);

    const handleClose = useCallback(() => {
        setEnlargedImage(null);
    }, []);

    // Group activities by date
    const groupedActivities = React.useMemo(() => {
        return activities.reduce((acc, activity) => {
            const dateKey = formatDate(activity.date);
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(activity);
            return acc;
        }, {});
    }, [activities]);

    if (activities.length === 0) {
        return (
            <Card className="text-center py-5">
                <Card.Body>
                    <i className="bi bi-calendar-x d-block mb-3" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
                    <p className="text-muted mb-0">No activities to display</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="activities-feed">
            {Object.entries(groupedActivities).map(([date, dateActivities]) => (
                <Card key={date} className="feed-date-group">
                    <div className="feed-date-header">{date}</div>
                    {dateActivities.map((activity) => (
                        <FeedActivityCard
                            key={activity.activityId}
                            activity={{
                                ...activity,
                                userGivenName: activity.userGivenName || `${activity.firstName} ${activity.lastName}`
                            }}
                            onUsernameClick={onUsernameClick}
                            onImageClick={handleImageClick}
                            resolvedAttachments={attachments}
                        />
                    ))}
                </Card>
            ))}
            <Modal
                show={!!enlargedImage}
                onHide={handleClose}
                centered
                size="lg"
            >
                <Modal.Body className="p-0 d-flex justify-content-center align-items-center">
                    {enlargedImage && (
                        <Image
                            src={enlargedImage}
                            fluid
                            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

FeedActivityCard.propTypes = {
    activity: PropTypes.shape({
        activityId: PropTypes.string.isRequired,
        activityType: PropTypes.string.isRequired,
        points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        duration: PropTypes.number.isRequired,
        username: PropTypes.string,
        userGivenName: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        profilePicture: PropTypes.string,
        description: PropTypes.string,
        photos: PropTypes.arrayOf(PropTypes.string),
        attachments: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired
        }))
    }).isRequired,
    onUsernameClick: PropTypes.func,
    onImageClick: PropTypes.func,
    resolvedAttachments: PropTypes.objectOf(PropTypes.string)
};

ActivitiesFeed.propTypes = {
    activities: PropTypes.array.isRequired,
    onUsernameClick: PropTypes.func.isRequired
};

export default ActivitiesFeed;
